import React, { useEffect, useRef } from 'react';
import './ParticlesCanvas.css';

const ParticlesCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    let canvasWidth = window.innerWidth;
    let canvasHeight = window.innerHeight;
    
    let fps = 30;
    let frameCount = 0;
    let lastFpsCheck = performance.now();
    
    let firstLowFpsDetected = 0;
    let firstReductionDone = false;
    let lastReductionTime = 0;
    let isWaitingBeforeFirstReduction = false;
    let isWaitingAfterReduction = false;
    
    let currentParticleCount = 150;
    let targetParticleCount = 150;
    
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isLowEndDevice = isMobile || navigator.hardwareConcurrency <= 4;
    
    if (isMobile) {
      targetParticleCount = 80;
      currentParticleCount = 80;
    } else if (isLowEndDevice) {
      targetParticleCount = 100;
      currentParticleCount = 100;
    }

    const particles = [];
    const baseConnectionDistance = 150;
    const baseMaxConnections = 10;

    let scrollProgress = 0;
    let smoothScrollProgress = 0;
    
    let pupilHoverProgress = 0;
    let isHoveringPupil = false;
    
    let fixedEyeCenterX = null;
    let fixedEyeCenterY = null;
    let fixedEyeScale = null;
    let isEyePositionFixed = false;
    
    function resizeCanvas() {
      canvasWidth = window.innerWidth;
      canvasHeight = window.innerHeight;
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      
      const isEyeFormingOrFormed = smoothScrollProgress >= 0.5;
      
      if (particles.length > 0 && !isEyeFormingOrFormed) {
        initParticles(currentParticleCount, 'resizeCanvas');
      }
    }
    resizeCanvas();
    
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(resizeCanvas, 100);
    });

    function getEyePosition(index, total, centerX, centerY, scale) {
      const normalizedScale = scale / 20;
      
      const outlineCount = Math.floor(total * 0.7);
      const pupilCount = total - outlineCount;
      
      if (index < outlineCount) {
        const angleStep = (Math.PI * 2) / outlineCount;
        const t = index * angleStep;
        
        const eyeWidth = 28;
        const eyeHeight = 14;
        
        const x = Math.cos(t) * eyeWidth;
        const y = Math.sin(t) * eyeHeight;
        
        return {
          x: centerX + x * normalizedScale,
          y: centerY + y * normalizedScale,
          group: 'outline'
        };
        
      } else {
        const pupilIndex = index - outlineCount;
        const angleStep = (Math.PI * 2) / pupilCount;
        const t = pupilIndex * angleStep;
        
        const pupilRadius = 7;
        
        return {
          x: centerX + Math.cos(t) * pupilRadius * normalizedScale,
          y: centerY + Math.sin(t) * pupilRadius * normalizedScale,
          group: 'pupil'
        };
      }
    }

    function lerp(start, end, amount) {
      return start + (end - start) * amount;
    }

    function initParticles(count, caller = 'unknown') {
      console.log('🔥 initParticles called from:', caller, {
        count,
        smoothScrollProgress: (smoothScrollProgress * 100).toFixed(1) + '%'
      });
      
      particles.length = 0;
      for (let i = 0; i < count; i++) {
        const randomX = Math.random() * canvasWidth;
        const randomY = Math.random() * canvasHeight;
        
        particles.push({
          x: randomX,
          y: randomY,
          homeX: randomX,
          homeY: randomY,
          driftAngle: Math.random() * Math.PI * 2,
          driftSpeed: 0.115 + Math.random() * 0.23,
          driftRadius: 34.5 + Math.random() * 23,
          pulseSpeed: 0.5 + Math.random() * 0.5,
          pulsePhase: Math.random() * Math.PI * 2,
          baseRadius: Math.random() * 2.5 + 0.5,
          radius: Math.random() * 2.5 + 0.5,
          baseOpacity: Math.random() * 0.6 + 0.3,
          opacity: Math.random() * 0.6 + 0.3,
          phase: Math.random() * Math.PI * 2,
          index: i,
          group: null
        });
      }
    }
    
    function adjustParticleCount(newCount, caller) {
      const currentCount = particles.length;
      
      if (newCount === currentCount) return;
      
      console.log(`🔧 Adjusting particles: ${currentCount} → ${newCount} (${caller})`);
      
      if (newCount < currentCount) {
        const removeCount = currentCount - newCount;
        particles.splice(-removeCount);
        console.log(`   ➖ Removed ${removeCount} particles`);
      } else {
        const addCount = newCount - currentCount;
        
        const isDrifting = smoothScrollProgress < 0.5;
        
        if (isDrifting) {
          console.log(`   ➕ Adding ${addCount} particles at RANDOM positions (drifting)`);
          for (let i = 0; i < addCount; i++) {
            const newIndex = currentCount + i;
            const randomX = Math.random() * canvasWidth;
            const randomY = Math.random() * canvasHeight;
            
            particles.push({
              x: randomX,
              y: randomY,
              homeX: randomX,
              homeY: randomY,
              driftAngle: Math.random() * Math.PI * 2,
              driftSpeed: 0.115 + Math.random() * 0.23,
              driftRadius: 34.5 + Math.random() * 23,
              pulseSpeed: 0.5 + Math.random() * 0.5,
              pulsePhase: Math.random() * Math.PI * 2,
              baseRadius: Math.random() * 2.5 + 0.5,
              radius: Math.random() * 2.5 + 0.5,
              baseOpacity: Math.random() * 0.6 + 0.3,
              opacity: Math.random() * 0.6 + 0.3,
              phase: Math.random() * Math.PI * 2,
              index: newIndex,
              group: null
            });
          }
        } else {
          console.log(`   ➕ Adding ${addCount} particles at EYE positions (forming)`);
          const infinityCenterX = canvasWidth / 2;
          const infinityCenterY = canvasHeight / 2;
          const infinityScale = Math.min(canvasWidth, canvasHeight) * 0.213;
          
          for (let i = 0; i < addCount; i++) {
            const newIndex = currentCount + i;
            const eyePos = getEyePosition(newIndex, newCount, infinityCenterX, infinityCenterY, infinityScale);
            
            particles.push({
              x: eyePos.x,
              y: eyePos.y,
              homeX: eyePos.x,
              homeY: eyePos.y,
              driftAngle: Math.random() * Math.PI * 2,
              driftSpeed: 0.115 + Math.random() * 0.23,
              driftRadius: 34.5 + Math.random() * 23,
              pulseSpeed: 0.5 + Math.random() * 0.5,
              pulsePhase: Math.random() * Math.PI * 2,
              baseRadius: Math.random() * 2.5 + 0.5,
              radius: Math.random() * 2.5 + 0.5,
              baseOpacity: Math.random() * 0.6 + 0.3,
              opacity: Math.random() * 0.6 + 0.3,
              phase: Math.random() * Math.PI * 2,
              index: newIndex,
              group: eyePos.group
            });
          }
        }
      }
    }

    initParticles(currentParticleCount, 'initial');

    let mouseX = -1000;
    let mouseY = -1000;
    
    document.addEventListener('mousemove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      
      if (smoothScrollProgress > 0.85) {
        const infinityCenterX = canvasWidth / 2;
        const infinityCenterY = canvasHeight / 2;
        const infinityScale = Math.min(canvasWidth, canvasHeight) * 0.213;
        const pupilRadius = 7 * (infinityScale / 20);
        
        const dx = mouseX - infinityCenterX;
        const dy = mouseY - infinityCenterY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        isHoveringPupil = distance < pupilRadius;
      } else {
        isHoveringPupil = false;
      }
    });

    document.addEventListener('touchmove', (e) => {
      if (e.touches.length > 0) {
        mouseX = e.touches[0].clientX;
        mouseY = e.touches[0].clientY;
      }
    }, { passive: true });

    document.addEventListener('touchend', () => {
      mouseX = -1000;
      mouseY = -1000;
      isHoveringPupil = false;
    });

    window.addEventListener('scroll', () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      scrollProgress = Math.min(scrollTop / maxScroll, 1);
    }, { passive: true });

    let lastTime = 0;
    const targetFps = 30;
    const fpsInterval = 1000 / targetFps;

    function animate(currentTime) {
      requestAnimationFrame(animate);
      
      const elapsed = currentTime - lastTime;
      if (elapsed < fpsInterval) return;
      
      lastTime = currentTime - (elapsed % fpsInterval);

      frameCount++;
      const elapsedFps = currentTime - lastFpsCheck;
      if (elapsedFps >= 1000) {
        fps = Math.round((frameCount * 1000) / elapsedFps);
        frameCount = 0;
        lastFpsCheck = currentTime;
        
        if (fps <= 29) {
          if (!firstReductionDone) {
            if (firstLowFpsDetected === 0) {
              firstLowFpsDetected = currentTime;
              isWaitingBeforeFirstReduction = true;
              console.log(`⏱️ Low FPS detected (${fps}), waiting 6 seconds before first reduction...`);
            } else if (isWaitingBeforeFirstReduction) {
              const waitTime = currentTime - firstLowFpsDetected;
              if (waitTime >= 6000) {
                console.log(`⚠️ 6 seconds passed with FPS=${fps}, doing FIRST reduction (-30)`);
                targetParticleCount = Math.max(0, currentParticleCount - 30);
                adjustParticleCount(targetParticleCount, `first_reduction_${fps}`);
                currentParticleCount = targetParticleCount;
                
                firstReductionDone = true;
                isWaitingBeforeFirstReduction = false;
                isWaitingAfterReduction = true;
                lastReductionTime = currentTime;
                
                console.log(`   🔻 First reduction complete: ${currentParticleCount} particles`);
              }
            }
          } else if (firstReductionDone && isWaitingAfterReduction) {
            const waitTime = currentTime - lastReductionTime;
            if (waitTime >= 6000) {
              console.log(`⚠️ 6 seconds passed with FPS=${fps}, doing incremental reduction (-10)`);
              targetParticleCount = Math.max(0, currentParticleCount - 10);
              adjustParticleCount(targetParticleCount, `incremental_reduction_${fps}`);
              currentParticleCount = targetParticleCount;
              
              lastReductionTime = currentTime;
              
              console.log(`   🔻 Reduced to ${currentParticleCount} particles`);
              
              if (currentParticleCount === 0) {
                console.log(`   ⚠️ Reached minimum (0 particles)`);
                isWaitingAfterReduction = false;
              }
            }
          }
        } else {
          if (isWaitingBeforeFirstReduction) {
            console.log(`✅ FPS recovered to ${fps} before first reduction, resetting timer`);
            firstLowFpsDetected = 0;
            isWaitingBeforeFirstReduction = false;
          }
        }
      }

      smoothScrollProgress = lerp(smoothScrollProgress, scrollProgress, 0.08);
      
      const smoothInfinityStrength = smoothScrollProgress >= 0.5 
        ? Math.min((smoothScrollProgress - 0.5) / 0.35, 1)
        : 0;

      const isFormingEye = smoothInfinityStrength > 0.3;
      const adaptiveMaxConnections = isFormingEye 
        ? (isMobile ? 3 : 5) 
        : (isMobile ? 5 : 10);

      const connectionDistance = baseConnectionDistance + Math.sin(smoothScrollProgress * Math.PI) * 20;
      const maxConnections = Math.min(
        baseMaxConnections + Math.floor(smoothScrollProgress * 3), 
        adaptiveMaxConnections
      );

      const baseR = 100, baseG = 200, baseB = 255;
      const targetR = 253, targetG = 178, targetB = 65;
      
      const currentR = lerp(baseR, targetR, smoothInfinityStrength * 0.8);
      const currentG = lerp(baseG, targetG, smoothInfinityStrength * 0.8);
      const currentB = lerp(baseB, targetB, smoothInfinityStrength * 0.3);

      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      let infinityCenterX, infinityCenterY, infinityScale;
      
      if (isEyePositionFixed && fixedEyeCenterX !== null) {
        infinityCenterX = fixedEyeCenterX;
        infinityCenterY = fixedEyeCenterY;
        infinityScale = fixedEyeScale;
      } else {
        infinityCenterX = canvasWidth / 2;
        infinityCenterY = canvasHeight / 2;
        infinityScale = Math.min(canvasWidth, canvasHeight) * 0.213;
      }

      if (smoothInfinityStrength > 0.85) {
        const breathe = Math.sin(currentTime * 0.0008) * 0.15 + 0.85;
        const auraSize = 180 * breathe;
        const auraOpacity = 0.25 * breathe;
        
        const auraGradient = ctx.createRadialGradient(
          infinityCenterX, infinityCenterY, 0,
          infinityCenterX, infinityCenterY, auraSize
        );
        auraGradient.addColorStop(0, `rgba(253, 178, 65, ${auraOpacity})`);
        auraGradient.addColorStop(0.4, `rgba(253, 178, 65, ${auraOpacity * 0.6})`);
        auraGradient.addColorStop(1, 'rgba(253, 178, 65, 0)');
        
        ctx.globalCompositeOperation = 'screen';
        ctx.beginPath();
        ctx.arc(infinityCenterX, infinityCenterY, auraSize, 0, Math.PI * 2);
        ctx.fillStyle = auraGradient;
        ctx.fill();
        ctx.globalCompositeOperation = 'source-over';
      }

      const isPupilShown = smoothScrollProgress >= 0.9;
      
      let settlingPercent = 0;
      if (isPupilShown && particles.length > 0) {
        const maxDistanceForPercent = 20;
        let totalSettlementScore = 0;
        
        for (let i = 0; i < particles.length; i++) {
          const particle = particles[i];
          const eyeTarget = getEyePosition(
            particle.index,
            particles.length,
            infinityCenterX,
            infinityCenterY,
            infinityScale
          );
          
          const dx = particle.x - eyeTarget.x;
          const dy = particle.y - eyeTarget.y;
          const distToTarget = Math.sqrt(dx * dx + dy * dy);
          
          const particleSettlement = Math.max(0, 1 - (distToTarget / maxDistanceForPercent));
          totalSettlementScore += particleSettlement;
        }
        
        settlingPercent = (totalSettlementScore / particles.length) * 100;
        
        if (settlingPercent >= 85 && !isEyePositionFixed) {
          fixedEyeCenterX = infinityCenterX;
          fixedEyeCenterY = infinityCenterY;
          fixedEyeScale = infinityScale;
          isEyePositionFixed = true;
          console.log('🔒 Eye position FIXED at:', {x: fixedEyeCenterX, y: fixedEyeCenterY, scale: fixedEyeScale});
        }
      }
      
      const allParticlesSettled = isPupilShown && settlingPercent >= 85;

      particles.forEach((particle, i) => {
        const eyeTarget = getEyePosition(
          particle.index,
          particles.length,
          infinityCenterX,
          infinityCenterY,
          infinityScale
        );

        if (isFormingEye && !particle.group) {
          particle.group = eyeTarget.group;
        }

        if (!isFormingEye) {
          particle.driftAngle += particle.driftSpeed * 0.0115;
          const driftX = Math.cos(particle.driftAngle) * particle.driftRadius;
          const driftY = Math.sin(particle.driftAngle) * particle.driftRadius;
          
          particle.homeX = particle.homeX || particle.x;
          particle.homeY = particle.homeY || particle.y;
          
          const driftTargetX = particle.homeX + driftX;
          const driftTargetY = particle.homeY + driftY;
          
          const targetX = lerp(driftTargetX, eyeTarget.x, smoothInfinityStrength);
          const targetY = lerp(driftTargetY, eyeTarget.y, smoothInfinityStrength);
          
          particle.x = lerp(particle.x, targetX, 0.009);
          particle.y = lerp(particle.y, targetY, 0.009);
          
          particle.group = null;
        } else if (isPupilShown && allParticlesSettled) {
          particle.x = eyeTarget.x;
          particle.y = eyeTarget.y;
        } else {
          particle.x = lerp(particle.x, eyeTarget.x, 0.02);
          particle.y = lerp(particle.y, eyeTarget.y, 0.02);
        }

        if (!isFormingEye) {
          const mouseStrength = isMobile ? 0.5 : 1;
          const dx = particle.x - mouseX;
          const dy = particle.y - mouseY;
          const distToMouse = Math.sqrt(dx * dx + dy * dy);
          
          if (distToMouse < 120 && mouseStrength > 0) {
            const force = (120 - distToMouse) / 120 * mouseStrength;
            particle.x += (dx / distToMouse) * force * 1.5;
            particle.y += (dy / distToMouse) * force * 1.5;
          }
        }

        particle.pulsePhase = (particle.pulsePhase || 0) + 0.01;
        const pulsation = Math.sin(particle.pulsePhase) * 0.3;
        particle.radius = particle.baseRadius + pulsation;
        
        const eyeBrightness = smoothInfinityStrength * 0.4;
        particle.opacity = particle.baseOpacity + eyeBrightness;
        particle.opacity = Math.min(Math.max(particle.opacity, 0.2), 1);

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        
        const gradient = ctx.createRadialGradient(
          particle.x, particle.y, 0,
          particle.x, particle.y, particle.radius * 2
        );
        
        gradient.addColorStop(0, `rgba(${Math.floor(currentR)}, ${Math.floor(currentG)}, ${Math.floor(currentB)}, ${particle.opacity})`);
        gradient.addColorStop(0.5, `rgba(${Math.floor(currentR * 0.9)}, ${Math.floor(currentG * 0.9)}, ${Math.floor(currentB * 0.8)}, ${particle.opacity * 0.7})`);
        gradient.addColorStop(1, `rgba(${Math.floor(currentR * 0.5)}, ${Math.floor(currentG * 0.5)}, ${Math.floor(currentB * 0.5)}, 0)`);
        
        ctx.fillStyle = gradient;
        ctx.fill();

        const normalizedScale = infinityScale / 20;
        const eyeWhiteRadiusX = 28 * normalizedScale;
        const eyeWhiteRadiusY = 14 * normalizedScale;
        
        const eyeWhiteRadius = (eyeWhiteRadiusX + eyeWhiteRadiusY) / 2;
        const pupilRadius = 7 * normalizedScale;
        
        const dx1 = particle.x - infinityCenterX;
        const dy1 = particle.y - infinityCenterY;
        const distFromCenter1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
        const isParticle1InsideEye = distFromCenter1 < eyeWhiteRadius * 0.9;

        let connections = 0;
        const step = isFormingEye ? 2 : 1;
        
        for (let j = i + 1; j < particles.length; j += step) {
          if (connections >= maxConnections) break;
          
          const otherParticle = particles[j];
          
          if (particle.group && otherParticle.group && particle.group !== otherParticle.group) {
            continue;
          }
          
          const dx2 = otherParticle.x - infinityCenterX;
          const dy2 = otherParticle.y - infinityCenterY;
          const distFromCenter2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
          const isParticle2InsideEye = distFromCenter2 < eyeWhiteRadius * 0.9;
          
          if (isFormingEye && isParticle1InsideEye && isParticle2InsideEye) {
            continue;
          }
          
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            
            if (particle.group && particle.group === otherParticle.group && smoothScrollProgress > 0.5) {
              
              const angle1 = Math.atan2(particle.y - infinityCenterY, particle.x - infinityCenterX);
              const angle2 = Math.atan2(otherParticle.y - infinityCenterY, otherParticle.x - infinityCenterX);
              
              let angleDiff = Math.abs(angle2 - angle1);
              if (angleDiff > Math.PI) {
                angleDiff = 2 * Math.PI - angleDiff;
              }
              
              const maxAngle = particle.group === 'pupil' 
                ? Math.PI / 6
                : Math.PI / 10;
              
              if (angleDiff > maxAngle) {
                continue;
              }
              
              if (particle.group === 'outline') {
                const arcRadius = (eyeWhiteRadiusX + eyeWhiteRadiusY) / 2;
                const arcLength = angleDiff * arcRadius;
                
                if (arcLength > distance * 1.5) {
                  continue;
                }
                
                const lineDistToCenter = Math.abs(
                  (otherParticle.y - particle.y) * infinityCenterX - 
                  (otherParticle.x - particle.x) * infinityCenterY + 
                  otherParticle.x * particle.y - 
                  otherParticle.y * particle.x
                ) / distance;
                
                if (lineDistToCenter < pupilRadius * 1.3) {
                  continue;
                }
              } else if (particle.group === 'pupil') {
                const arcLength = angleDiff * pupilRadius;
                if (arcLength > distance * 1.3) {
                  continue;
                }
              }
            }
            
            connections++;
            
            const distanceOpacity = (1 - distance / connectionDistance) * 0.25;
            const infinityBoost = smoothInfinityStrength * 0.3;
            const opacity = distanceOpacity + infinityBoost;
            
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            
            const connectionR = lerp(currentR, 253, smoothInfinityStrength);
            const connectionG = lerp(currentG, 178, smoothInfinityStrength);
            const connectionB = lerp(currentB, 65, smoothInfinityStrength);
            
            ctx.strokeStyle = `rgba(${Math.floor(connectionR)}, ${Math.floor(connectionG)}, ${Math.floor(connectionB)}, ${opacity})`;
            ctx.lineWidth = smoothInfinityStrength > 0.85 ? 2 : 1;
            ctx.stroke();
          }
        }
      });

      const hoverSpeed = 1 / (5 * 30);
      if (isHoveringPupil) {
        pupilHoverProgress = Math.min(1, pupilHoverProgress + hoverSpeed);
      } else {
        pupilHoverProgress = Math.max(0, pupilHoverProgress - hoverSpeed);
      }

      if (smoothScrollProgress > 0.85) {
        const normalizedScale = infinityScale / 20;

        const pupilFadeProgress = smoothScrollProgress >= 0.9
          ? 1
          : (smoothScrollProgress - 0.85) / 0.05;

        const PURPLE = {
          outline: [110, 170, 255],
          glow:    [80, 150, 255],
          core1:   [255, 210, 120],
          core2:   [255, 180, 80],
          core3:   [200, 140, 60],
          edge:    [70, 120, 200],
          iris:    [140, 200, 255],
          highlight1: [255, 235, 190],
          highlight2: [180, 220, 255]
        };

        ctx.beginPath();
        const eyeWidth = 28 * normalizedScale;
        const eyeHeight = 14 * normalizedScale;
        ctx.ellipse(infinityCenterX, infinityCenterY, eyeWidth, eyeHeight, 0, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${PURPLE.outline.join(',')}, 0.75)`;
        ctx.lineWidth = 2.5;
        ctx.stroke();

        const pupilRadius = 7 * normalizedScale;
        const glowPulse = Math.sin(currentTime * 0.002) * 0.2 + 0.8;

        ctx.shadowBlur = 22 * pupilFadeProgress;
        ctx.shadowColor = `rgba(${PURPLE.glow.join(',')}, ${glowPulse * 0.6 * pupilFadeProgress})`;

        const pupilGradient = ctx.createRadialGradient(
          infinityCenterX, infinityCenterY, 0,
          infinityCenterX, infinityCenterY, pupilRadius
        );

        pupilGradient.addColorStop(0,   `rgba(${PURPLE.core1.join(',')}, ${0.95 * pupilFadeProgress})`);
        pupilGradient.addColorStop(0.35,`rgba(${PURPLE.core2.join(',')}, ${0.9  * pupilFadeProgress})`);
        pupilGradient.addColorStop(0.65,`rgba(${PURPLE.core3.join(',')}, ${0.85 * pupilFadeProgress})`);
        pupilGradient.addColorStop(1,   `rgba(${PURPLE.edge.join(',')},  ${0.75 * pupilFadeProgress})`);

        ctx.beginPath();
        ctx.arc(infinityCenterX, infinityCenterY, pupilRadius, 0, Math.PI * 2);
        ctx.fillStyle = pupilGradient;
        ctx.fill();

        ctx.shadowBlur = 0;

        const irisLineCount = 24;
        const irisInnerRadius = pupilRadius * 0.2;
        const irisOuterRadius = pupilRadius * 0.98;

        ctx.globalAlpha = 0.6 * pupilFadeProgress;
        for (let i = 0; i < irisLineCount; i++) {
          const angle = (i / irisLineCount) * Math.PI * 2;
          ctx.beginPath();
          ctx.moveTo(
            infinityCenterX + Math.cos(angle) * irisInnerRadius,
            infinityCenterY + Math.sin(angle) * irisInnerRadius
          );
          ctx.lineTo(
            infinityCenterX + Math.cos(angle) * irisOuterRadius,
            infinityCenterY + Math.sin(angle) * irisOuterRadius
          );
          ctx.strokeStyle = `rgba(${PURPLE.iris.join(',')}, ${0.7 * pupilFadeProgress})`;
          ctx.lineWidth = 1;
          ctx.stroke();
        }
        ctx.globalAlpha = 1;

        const baseSize = 0.55;
        const hoverSize = 0.9;
        const currentSize = lerp(baseSize, hoverSize, pupilHoverProgress);
        const centerDotRadius = pupilRadius * currentSize;

        ctx.beginPath();
        ctx.arc(infinityCenterX, infinityCenterY, centerDotRadius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 0, 0, ${0.9 * pupilFadeProgress})`;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(infinityCenterX, infinityCenterY, pupilRadius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${PURPLE.outline.join(',')}, ${0.9 * pupilFadeProgress})`;
        ctx.lineWidth = 2.5;
        ctx.stroke();

        const highlightGradient = ctx.createRadialGradient(
          infinityCenterX - pupilRadius * 0.3,
          infinityCenterY - pupilRadius * 0.3,
          0,
          infinityCenterX - pupilRadius * 0.3,
          infinityCenterY - pupilRadius * 0.3,
          pupilRadius * 0.5
        );

        highlightGradient.addColorStop(0, `rgba(${PURPLE.highlight1.join(',')}, ${glowPulse * 0.6 * pupilFadeProgress})`);
        highlightGradient.addColorStop(0.5, `rgba(${PURPLE.highlight2.join(',')}, ${glowPulse * 0.3 * pupilFadeProgress})`);
        highlightGradient.addColorStop(1, 'rgba(200, 150, 255, 0)');

        ctx.beginPath();
        ctx.arc(
          infinityCenterX - pupilRadius * 0.3,
          infinityCenterY - pupilRadius * 0.3,
          pupilRadius * 0.5,
          0, Math.PI * 2
        );
        ctx.fillStyle = highlightGradient;
        ctx.fill();

        const pulse = Math.sin(currentTime * 0.0015) * 0.08 + 0.92;
        const dynamicWidth = eyeWidth * pulse * 1.15;
        const dynamicHeight = eyeHeight * pulse * 1.15;

        ctx.beginPath();
        ctx.ellipse(infinityCenterX, infinityCenterY, dynamicWidth, dynamicHeight, 0, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${PURPLE.outline.join(',')}, ${0.3 + pulse * 0.2})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      updateDebugPanel(smoothScrollProgress, smoothInfinityStrength, currentTime);
    }

    animate(0);

    const shouldShowDebug = window.location.hash.toLowerCase().includes('дебаг') || 
                            window.location.hash.toLowerCase().includes('debug');
    
    let debugPanel = null;
    if (shouldShowDebug) {
      debugPanel = document.createElement('div');
      debugPanel.id = 'debug-panel';
      debugPanel.style.cssText = `
        position: fixed;
        top: 80px;
        right: 20px;
        background: rgba(0, 0, 0, 0.9);
        color: #FDB241;
        padding: 15px 20px;
        border-radius: 12px;
        font-family: monospace;
        font-size: 13px;
        z-index: 9999;
        backdrop-filter: blur(10px);
        border: 2px solid rgba(253, 178, 65, 0.4);
        min-width: 280px;
        line-height: 1.6;
      `;
      document.body.appendChild(debugPanel);
      
      console.log('🐛 Debug panel enabled');
    } else {
      console.log('✅ Debug panel disabled');
    }
    
    function updateDebugPanel(smoothScroll, infinityStrength, currentTime) {
      if (!debugPanel) return;
      
      const isPupilVisible = smoothScroll >= 0.9;
      let particlesSettled = false;
      let settlingPercent = 0;
      
      if (isPupilVisible && particles.length > 0) {
        const maxDistanceForPercent = 20;
        const infinityCenterX = canvasWidth / 2;
        const infinityCenterY = canvasHeight / 2;
        const infinityScale = Math.min(canvasWidth, canvasHeight) * 0.213;
        
        let totalSettlementScore = 0;
        
        for (let i = 0; i < particles.length; i++) {
          const particle = particles[i];
          const eyeTarget = getEyePosition(
            particle.index,
            particles.length,
            infinityCenterX,
            infinityCenterY,
            infinityScale
          );
          
          const dx = particle.x - eyeTarget.x;
          const dy = particle.y - eyeTarget.y;
          const distToTarget = Math.sqrt(dx * dx + dy * dy);
          
          const particleSettlement = Math.max(0, 1 - (distToTarget / maxDistanceForPercent));
          totalSettlementScore += particleSettlement;
        }
        
        settlingPercent = Math.round((totalSettlementScore / particles.length) * 100);
        particlesSettled = settlingPercent >= 85;
      }
      
      let phase = '';
      let phaseColor = '#64748B';
      
      if (smoothScroll < 0.5) {
        phase = '🌀 Drifting';
        phaseColor = '#3B82F6';
      } else if (smoothScroll >= 0.5 && smoothScroll < 0.85) {
        const formationPercent = Math.round(((smoothScroll - 0.5) / 0.35) * 100);
        phase = `⚡ Forming... ${formationPercent}%`;
        phaseColor = '#F59E0B';
      } else if (smoothScroll >= 0.85 && smoothScroll < 0.9) {
        const pupilPercent = Math.round(((smoothScroll - 0.85) / 0.05) * 100);
        phase = `👁️ Eye Formed + Pupil ${pupilPercent}%`;
        phaseColor = '#10B981';
      } else if (smoothScroll >= 0.9 && !particlesSettled) {
        phase = `⏳ Settling... ${settlingPercent}%`;
        phaseColor = '#F59E0B';
      } else if (smoothScroll >= 0.9 && particlesSettled) {
        phase = '🔥 Pupil Active + FIXED';
        phaseColor = '#EF4444';
      }
      
      const fpsColor = fps < 29 ? '#EF4444' : fps < 25 ? '#DC2626' : '#10B981';
      
      let optimizationStatus = '';
      if (isWaitingBeforeFirstReduction && firstLowFpsDetected > 0) {
        const elapsed = Math.max(0, Math.round((currentTime - firstLowFpsDetected) / 1000));
        const remaining = Math.max(0, 6 - elapsed);
        optimizationStatus = `<div style="color: #F59E0B; margin-top: 8px;">⏱️ Waiting ${remaining}s before -30</div>`;
      } else if (firstReductionDone && isWaitingAfterReduction && lastReductionTime > 0) {
        const elapsed = Math.max(0, Math.round((currentTime - lastReductionTime) / 1000));
        const remaining = Math.max(0, 6 - elapsed);
        optimizationStatus = `<div style="color: #F59E0B; margin-top: 8px;">⏱️ Waiting ${remaining}s before -10</div>`;
      } else if (firstReductionDone) {
        optimizationStatus = `<div style="color: #10B981; margin-top: 8px;">✅ Optimizations active</div>`;
      }
      
      const particlesStatus = smoothScroll >= 0.9 
        ? (particlesSettled 
            ? '<span style="color: #EF4444;">FIXED</span>' 
            : '<span style="color: #F59E0B;">SETTLING</span>')
        : '<span style="color: #10B981;">MOVING</span>';
      
      debugPanel.innerHTML = `
        <div style="font-weight: 700; font-size: 15px; margin-bottom: 10px; color: #FDB241;">
          🔍 EYE DEBUG PANEL
        </div>
        <div style="margin-bottom: 8px;">
          <span style="color: #94A3B8;">FPS:</span> 
          <span style="color: ${fpsColor}; font-weight: 600;">${fps}</span>
        </div>
        <div style="margin-bottom: 8px;">
          <span style="color: #94A3B8;">Particles:</span> 
          <span style="color: #E2E8F0; font-weight: 600;">${currentParticleCount}</span>
          (${particlesStatus})
        </div>
        <div style="margin-bottom: 8px;">
          <span style="color: #94A3B8;">Settling:</span> 
          <span style="color: ${settlingPercent >= 85 ? '#10B981' : '#F59E0B'}; font-weight: 600;">${settlingPercent}%</span>
        </div>
        <div style="margin-bottom: 8px;">
          <span style="color: #94A3B8;">Eye Position:</span> 
          <span style="color: ${isEyePositionFixed ? '#EF4444' : '#10B981'}; font-weight: 600;">${isEyePositionFixed ? 'FIXED' : 'DYNAMIC'}</span>
        </div>
        ${optimizationStatus}
        <div style="height: 1px; background: rgba(253, 178, 65, 0.2); margin: 12px 0;"></div>
        <div style="margin-bottom: 8px;">
          <span style="color: #94A3B8;">Phase:</span><br />
          <span style="color: ${phaseColor}; font-weight: 700; font-size: 14px;">${phase}</span>
        </div>
      `;
    }

    window.addEventListener('scroll', () => {
      const navbar = document.getElementById('navbar');
      if (navbar && window.scrollY > 50) {
        navbar.classList.add('scrolled');
      } else if (navbar) {
        navbar.classList.remove('scrolled');
      }
    });

    const scrollIndicator = document.querySelector('.scroll-indicator');
    if (scrollIndicator) {
      scrollIndicator.style.cursor = 'pointer';
      scrollIndicator.addEventListener('click', () => {
        const sections = document.querySelectorAll('section');
        const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
        
        let nextSection = null;
        for (let section of sections) {
          const sectionTop = section.offsetTop;
          if (sectionTop > scrollPosition + 100) {
            nextSection = section;
            break;
          }
        }
        
        if (nextSection) {
          nextSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }

    // Cleanup
    return () => {
      window.removeEventListener('resize', () => {});
    };
  }, []);

  return <canvas ref={canvasRef} id="particles-canvas" />;
};

export default ParticlesCanvas;
